/**
 * Subscribe to /api/activities stream
 * @param {function} onMessage - callback when new data arrives
 * @param {function} onError - callback when error occurs (optional)
 * @param {function} onConnectionChange - callback when connection status changes (optional)
 */
export const subscribeActivities = (onMessage, onError, onConnectionChange, rollingWindowSeconds = 43200) => {
  console.log("Subscribing to activities SSE...");
  
  let reconnectAttempts = 0;
  const maxReconnectAttempts = 5;
  const baseReconnectDelay = 1000; // 1 second
  
  const connect = () => {
    const es = new EventSource(`/api/activities?rolling_window=${rollingWindowSeconds}`);

    es.onopen = () => {
      console.log("Activities SSE connection opened");
      reconnectAttempts = 0;
      onConnectionChange?.({ status: 'connected', attempts: 0 });
    };

    es.onmessage = (event) => {
      try {
        // Skip heartbeat comments
        if (!event.data || event.data.trim() === '') return;
        
        console.log("Raw activities SSE data:", event.data);
        const data = JSON.parse(event.data);
        
        // Skip connection confirmation messages
        if (data.type === "connection") {
          console.log("Activities SSE connected successfully");
          return;
        }
        
        if (data.type === "error") {
          console.error("Activities SSE backend error:", data.message);
          onError?.(data.message);
          return;
        }
        
        onMessage(data);
      } catch (err) {
        console.error("Failed to parse activities SSE data:", err, "Raw data:", event.data);
        onError?.(`Parse error: ${err.message}`);
      }
    };

    es.onerror = (err) => {
      console.error("Activities SSE connection error:", err);
      console.error("EventSource readyState:", es.readyState);
      
      if (es.readyState === EventSource.CLOSED) {
        console.error("Activities SSE connection is closed");
        onConnectionChange?.({ status: 'disconnected', attempts: reconnectAttempts });
        
        if (reconnectAttempts < maxReconnectAttempts) {
          reconnectAttempts++;
          const delay = baseReconnectDelay * Math.pow(2, reconnectAttempts - 1); // Exponential backoff
          console.log(`Attempting to reconnect in ${delay}ms (attempt ${reconnectAttempts}/${maxReconnectAttempts})`);
          
          setTimeout(() => {
            console.log("Reconnecting to activities SSE...");
            connect();
          }, delay);
        } else {
          console.error("Max reconnection attempts reached for activities SSE");
          onError?.("Max reconnection attempts reached");
        }
      } else if (es.readyState === EventSource.CONNECTING) {
        console.warn("Activities SSE is connecting/reconnecting...");
        onConnectionChange?.({ status: 'connecting', attempts: reconnectAttempts });
      }
    };

    return es;
  };

  return connect();
};

/**
 * Fetch action center data
 * @param {object} handlers - callbacks for different event types
 * @param {number} rollingWindowSeconds - rolling window parameter
 */
export const fetchActionCenter = async (handlers, rollingWindowSeconds = 43200) => {
  
  try {
    const response = await fetch(`/api/action_center?rolling_window=${rollingWindowSeconds}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    // Transform the data to match the expected format
    const transformedData = {
      top_alerts: data // The API returns the alert array directly
    };

    // 🔹 Fetch alert details for all alert_ids in parallel
    const enrichedAlerts = await Promise.all(
      (transformedData.top_alerts || []).map(async (alert) => {
        try {
          if (!alert.alert_ids || alert.alert_ids.length === 0) {
            return { ...alert, details: [] };
          }

          // Fetch details in batch
          const query = alert.alert_ids.map(id => `ids=${id}`).join("&");
          const res = await fetch(`/api/get_alerts_detail?${query}`);
          const details = await res.json();
          return { ...alert, details };
        } catch (err) {
          console.error("Failed to load details for", alert.alert_title, err);
          return { ...alert, details: [] };
        }
      })
    );

    const finalData = {
      ...transformedData,
      top_alerts: enrichedAlerts,
    };

    handlers.onActionCenter?.(finalData);
    return finalData;

  } catch (error) {
    console.error("Failed to fetch action center data:", error);
    handlers.onError?.(error.message);
    throw error;
  }
};
